/**
 * @fileoverview Shared User Settings Page
 * @summary Reusable settings page for both organizers and singers
 * 
 * @package KaraoNow
 * @module apps/front/src/pages/UserSettingsPage
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../hooks/redux.js'
import PageHeader from '../components/PageHeader.js'
import Spinner from '../components/Spinner.js'
import { useTranslation } from 'react-i18next'
import { apiRequest } from '../services/api.js'
import { API_ENDPOINTS } from '../config/api.js'
import type { ReactNode } from 'react'

interface UserSettingsPageProps {
  /**
   * i18n translation key prefix (e.g. 'singer' or 'organizer')
   */
  i18nPrefix: string

  /**
   * Optional component to render before Profile section
   */
  preProfileComponent?: ReactNode

  /**
   * Profile form component to render
   */
  profileFormComponent: ReactNode

  /**
   * Optional component to render before Account Data section
   */
  preAccountDataComponent?: ReactNode

  /**
   * Additional menu items (optional)
   */
  additionalSections?: Array<{
    title: string
    description: string
    buttonLabel: string
    buttonVariant: 'primary' | 'outline' | 'error'
    action: () => void
  }>
}

/**
 * @summary Shared User Settings Page component
 * @param {UserSettingsPageProps} props - Configuration props
 * @returns {React.ReactElement} User Settings Page
 */
export default function UserSettingsPage({
  i18nPrefix,
  preProfileComponent,
  profileFormComponent,
  preAccountDataComponent,
  additionalSections = [],
}: UserSettingsPageProps): React.ReactElement {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const user = useAppSelector((state: any) => state.auth.user)

  // UI state
  const [loading, setLoading] = useState(true)
  const [passwordResetError, setPasswordResetError] = useState<string | null>(null)
  const [passwordResetSuccess, setPasswordResetSuccess] = useState<string | null>(null)
  const [passwordResetting, setPasswordResetting] = useState(false)

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }
    setLoading(false)
  }, [user, navigate])

  // Send password reset link
  const handleSendPasswordResetLink = async () => {
    try {
      setPasswordResetError(null)
      setPasswordResetting(true)

      const response = await apiRequest({
        method: 'POST',
        url: API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
        data: { email: user?.email },
      })

      if (!response.success) {
        setPasswordResetError(response.message || t('errors.resetPasswordFailed'))
        return
      }

      setPasswordResetSuccess(t(`${i18nPrefix}.passwordResetLinkSent`))
      setTimeout(() => setPasswordResetSuccess(null), 5000)
    } catch (err: any) {
      setPasswordResetError(err?.message || t('errors.resetPasswordFailed'))
    } finally {
      setPasswordResetting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-base-50 dark:bg-base-900">
      <PageHeader title={t(`${i18nPrefix}.settings`)} subtitle={t(`${i18nPrefix}.settingsSubtitle`)} />

      {passwordResetError && (
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="alert alert-error">
            <span>{passwordResetError}</span>
          </div>
        </div>
      )}

      {passwordResetSuccess && (
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="alert alert-success">
            <span>{passwordResetSuccess}</span>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Pre-Profile Component (e.g., PlanSection for organizers) */}
        {preProfileComponent}

        {/* Profile Form Component (passed as prop) */}
        {profileFormComponent}

        {/* Change Password Section */}
        <div className="card bg-base-100 dark:bg-base-800 shadow-lg">
          <div className="card-body">
            <h2 className="card-title text-xl mb-4">{t(`${i18nPrefix}.changePassword`)}</h2>
            <p className="text-sm text-base-600 dark:text-base-400 mb-4">
              {t(`${i18nPrefix}.changePasswordDescription`)}
            </p>
            <button
              className="btn btn-outline w-full!"
              onClick={handleSendPasswordResetLink}
              disabled={passwordResetting}
            >
              {passwordResetting ? (
                <>
                  <Spinner size="sm" />
                  {/* {t('common.loading')} */}
                </>
              ) : (
                t(`${i18nPrefix}.sendResetLink`)
              )}
            </button>
          </div>
        </div>

        {/* Additional Sections (configurable) */}
        {additionalSections.map((section, idx) => (
          <div key={idx} className="card bg-base-100 dark:bg-base-800 shadow-lg">
            <div className="card-body">
              <h2 className="card-title text-xl mb-4">{section.title}</h2>
              <p className="text-sm text-base-600 dark:text-base-400 mb-4">
                {section.description}
              </p>
              <button
                className={`btn btn-${section.buttonVariant} w-full!`}
                onClick={section.action}
              >
                {section.buttonLabel}
              </button>
            </div>
          </div>
        ))}

        {/* Pre-Account Data Component (e.g., PlanSection for organizers) */}
        {preAccountDataComponent}
      </div>
    </div>
  )
}
